/**
 * The panel — level two of D1: everything that changed, whenever somebody wants it.
 *
 * Three sections in one scroller: the notices valid right now, what is new for this reader,
 * then the complete history. The history is never filtered and never paged: it lives in the
 * bundle, it is a few dozen lines, and "I know it changed, I just cannot find when" is the
 * question this half answers.
 *
 * ## Current notices — D5 of the Reporting App's central-notices plan (M4, 2026-09-24)
 *
 * A notice closed in the bar is still readable here, for exactly as long as it is valid. The
 * section shows what the wire carries and nothing more: no dismissed flag, no archive, and a
 * notice whose window closes while the panel is open leaves the section on its own timer —
 * **an expired notice appears nowhere** (D5). The count is in the heading, and the button's dot
 * does NOT light for a notice (D8): the dot belongs to the entries, and a dot that kept shining
 * after the bar was closed would be the wallpaper the entries' own D7 forbids.
 *
 * ## Opening the panel is what clears the dot — and not the markers
 *
 * `openPanel()` marks the new entries seen (the provider does it, in the same click). The
 * markers on the elements survive that, deliberately: they go when the element is USED. So the
 * "New" section is frozen at the moment of opening — otherwise the list would empty itself
 * under the reader's eyes the instant the POST returned.
 *
 * ## "Show me"
 *
 * Closes the panel, asks the app to reveal the place, and highlights it for a moment. It is
 * offered only for an entry whose anchor is mounted right now AND was registered with a way to
 * reveal it: an offer that would do nothing is worse than no offer, and only the app knows
 * whether the element exists on this screen and how to get there.
 *
 * ## The images
 *
 * Fetched with the bearer token and shown from a blob URL (D4) — an `<img src>` cannot carry an
 * `Authorization` header, and a screenshot of this app carries a customer name, a turnover and
 * a margin, so it may not sit in the anonymous bundle. A failure renders as a thin line of
 * text, never as a broken-image icon: the entry is still worth reading without its picture.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowRight, Info, X } from 'lucide-react';
import { useWhatsNew } from './WhatsNewProvider';
import type { Notice, WhatsNewEntry, WhatsNewKind } from './types';

/** One word per kind, in the reader's language — not the repository's. */
const KIND_LABEL: Record<WhatsNewKind, string> = {
    new: 'New',
    improved: 'Improved',
    fixed: 'Fixed',
};

function EntryImage({ imageId }: { imageId: string }) {
    const { loadImage } = useWhatsNew();
    const [url, setUrl] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        void loadImage(imageId).then(result => {
            if (cancelled) return;
            if (result) setUrl(result);
            else setFailed(true);
        });
        return () => {
            cancelled = true;
        };
        // The provider owns the blob URLs and revokes them when the panel closes; revoking here
        // would pull the picture out from under a second entry using the same image.
    }, [imageId, loadImage]);

    if (failed) return <p className="mt-2 text-xs text-muted">The picture for this entry could not be loaded.</p>;
    if (!url) return <div className="mt-2 h-24 w-full animate-pulse rounded-md bg-head" />;

    return (
        <img
            src={url}
            alt=""
            className="mt-2 w-full rounded-md border border-hair"
            loading="lazy"
        />
    );
}

function EntryCard({ entry, highlight }: { entry: WhatsNewEntry; highlight: boolean }) {
    const { showMe, canRevealAnchor, tones } = useWhatsNew();
    const canShow = !!entry.anchor && canRevealAnchor(entry.anchor);

    return (
        <li
            className={`rounded-lg border p-3 ${
                highlight ? 'border-brand-100 bg-brand-50' : 'border-hair bg-surface'
            }`}
        >
            <div className="flex items-center gap-2">
                <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${tones.kind[entry.kind]}`}>
                    {KIND_LABEL[entry.kind]}
                </span>
                <time className="text-xs text-muted" dateTime={entry.date}>
                    {entry.date}
                </time>
            </div>
            <h3 className="mt-1.5 text-sm font-bold text-ink">{entry.title}</h3>
            <p className="mt-0.5 text-sm leading-snug text-body">{entry.body}</p>
            {entry.image && <EntryImage imageId={entry.image} />}
            {canShow && (
                <button
                    type="button"
                    onClick={() => showMe(entry)}
                    className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-info hover:underline"
                >
                    Show me
                    <ArrowRight className="h-3 w-3" aria-hidden="true" />
                </button>
            )}
        </li>
    );
}

/**
 * The notices whose window is still open, re-checked at the earliest end among them — the same
 * one-timer rule as the bar's, so a panel left open does not go on showing a notice that is over.
 */
function useCurrentNotices(notices: readonly Notice[]): readonly Notice[] {
    const [tick, setTick] = useState(0);

    const current = useMemo(() => {
        const now = Date.now();
        return notices.filter(n => {
            const until = Date.parse(n.validUntil);
            return Number.isNaN(until) || until > now;
        });
        // `tick` is the clock; see NoticeBar.
    }, [notices, tick]);

    useEffect(() => {
        const ends = current.map(n => Date.parse(n.validUntil)).filter(t => !Number.isNaN(t) && t > Date.now());
        if (ends.length === 0) return;
        const delay = Math.min(Math.min(...ends) - Date.now() + 1000, 2_000_000_000);
        const timer = window.setTimeout(() => setTick(t => t + 1), delay);
        return () => window.clearTimeout(timer);
    }, [current]);

    return current;
}

/** "Until Sat 26 Sep, 09:00" in the reader's own time zone, never the UTC of the wire. */
function untilLabel(validUntil: string): string {
    const d = new Date(validUntil);
    if (Number.isNaN(d.getTime())) return '';
    return `Until ${d.toLocaleString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    })}`;
}

function NoticeCard({ notice }: { notice: Notice }) {
    const { tones } = useWhatsNew();
    const style = tones.notice[notice.severity] ?? tones.notice.info;
    const Icon = notice.severity === 'info' ? Info : AlertTriangle;
    return (
        <li data-panel-notice-id={notice.id} className={`flex items-start gap-2.5 rounded-lg border p-3 ${style.bar}`}>
            <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${style.icon}`} aria-hidden="true" />
            <div className="min-w-0 flex-1">
                <h3 className="text-sm font-bold leading-tight">{notice.title}</h3>
                <p className="mt-0.5 text-sm leading-snug">{notice.body}</p>
                <p className="mt-1 text-xs">{untilLabel(notice.validUntil)}</p>
            </div>
        </li>
    );
}

export function WhatsNewPanel() {
    const { panelOpen, closePanel, panelNewEntries, entries, notices, tones } = useWhatsNew();
    const currentNotices = useCurrentNotices(notices);

    /**
     * Focus in on open, and back where it came from on close — S2-24 of the audit, DU6.
     *
     * The panel is `role="dialog"` and took no focus at all. It renders at the END of the page,
     * so it sat last in tab order: a reader who opened it with the keyboard had to walk the
     * whole app to reach it, and the X and "Show me" dropped focus to `<body>` when they
     * unmounted it. None of that is visible to a mouse.
     *
     * **The heading takes it, not the first control.** The panel is a reference somebody reads;
     * opening it with the close button pre-selected invites the Enter key that was already on
     * its way (the same argument as `ui/Modal.tsx`'s). `tabIndex={-1}` makes a heading
     * focusable without putting it in the tab order.
     *
     * **This is deliberately NOT a focus trap.** The panel is dimmed but not modal — clicking
     * the app behind it is a legitimate way to close it, and the blocking dialog is the one that
     * traps. Tab therefore walks out of it into the page, which is what a non-modal dialog does.
     *
     * The restore is in the cleanup, so it also runs when "Show me" closes the panel. "Show me"
     * then focuses the anchor it revealed **in a later frame** (`useWhatsNewAnchor`), so the
     * anchor wins over this restore rather than racing it.
     */
    const headingRef = useRef<HTMLHeadingElement>(null);
    /** The panel's own box: what "outside" means for the listener above. */
    const panelRef = useRef<HTMLElement>(null);
    useEffect(() => {
        if (!panelOpen) return;
        const returnTo = document.activeElement;
        headingRef.current?.focus();
        return () => {
            // Only if focus is still ours. A reader who clicked into the app behind the panel
            // has already chosen where they are, and yanking them back would be the panel
            // stealing focus on its way out.
            const active = document.activeElement;
            if (active === null || active === document.body || headingRef.current?.contains(active)) {
                (returnTo as HTMLElement | null)?.focus?.();
            }
        };
    }, [panelOpen]);

    useEffect(() => {
        if (!panelOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closePanel();
        };
        // `mousedown` and not `click`, for the reason the other two menus in this app use it: a
        // click that STARTED inside the panel and ended outside it (a drag over the scrollbar,
        // a text selection) is not an outside click, and `click` fires on the common ancestor
        // for both. The panel itself is the boundary, so a click on the dim layer — which is
        // outside the `<aside>` — closes, exactly as before.
        const onDocMouseDown = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) closePanel();
        };
        window.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onDocMouseDown);
        return () => {
            window.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onDocMouseDown);
        };
    }, [panelOpen, closePanel]);

    if (!panelOpen) return null;

    const newIds = new Set(panelNewEntries.map(e => e.id));

    return (
        <>
            {/* Dimmed, but not modal: the panel is a reference, and clicking the app behind it
                is a legitimate way to close it. The dialog is the one that blocks.

                The dim layer is DECORATION and carries no handler. It used to close the panel on
                click, which is the right behaviour and was the wrong way to get it: an `onClick`
                on a non-interactive element is an action a mouse can take and a keyboard cannot
                (WCAG 2.1.1), and `npm run scan:interactive` reports it. The behaviour is kept and
                moved to a document listener above — which is how `ui/ColumnFilter` and
                `ui/ColumnChooser` already do it, and the kit's `Dialog` says in as many words
                that closing on an outside click "is the right rule for a menu". Escape and the
                named Close button are the keyboard's two ways out, unchanged. */}
            <div className="fixed inset-0 z-40 bg-ink/20" aria-hidden="true" />
            <aside
                ref={panelRef}
                role="dialog"
                aria-label="What's New"
                className={`fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l border-hair bg-surface ${tones.overlay}`}
            >
                <header className="flex items-center justify-between border-b border-hair px-4 py-3">
                    <h2 ref={headingRef} tabIndex={-1} className="text-sm font-bold text-ink">
                        What&apos;s New
                    </h2>
                    <button
                        type="button"
                        onClick={closePanel}
                        aria-label="Close"
                        className="inline-flex min-h-8 min-w-8 items-center justify-center rounded-md p-1 text-muted hover:bg-head hover:text-body"
                    >
                        <X className="h-4 w-4" aria-hidden="true" />
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-3">
                    {currentNotices.length > 0 && (
                        <section className="mb-5" aria-label="Current notices">
                            <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted">
                                Current notices ({currentNotices.length})
                            </h3>
                            <ul className="flex flex-col gap-2">
                                {currentNotices.map(notice => (
                                    <NoticeCard key={notice.id} notice={notice} />
                                ))}
                            </ul>
                        </section>
                    )}

                    {panelNewEntries.length > 0 && (
                        <section className="mb-5">
                            <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted">
                                New for you
                            </h3>
                            <ul className="flex flex-col gap-2">
                                {panelNewEntries.map(entry => (
                                    <EntryCard key={entry.id} entry={entry} highlight />
                                ))}
                            </ul>
                        </section>
                    )}

                    <section>
                        <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted">
                            Everything so far
                        </h3>
                        {entries.length === 0 ? (
                            // Honest rather than clever: an empty history is what a freshly
                            // adopted module looks like, and it says so.
                            <p className="text-sm text-muted">Nothing has been recorded yet.</p>
                        ) : (
                            <ul className="flex flex-col gap-2">
                                {entries.map(entry => (
                                    <EntryCard key={entry.id} entry={entry} highlight={false} />
                                ))}
                            </ul>
                        )}
                    </section>
                </div>

                <footer className="border-t border-hair px-4 py-2">
                    <p className="text-xs text-muted">
                        {newIds.size > 0
                            ? `${newIds.size} new since you last looked`
                            : 'You are up to date'}
                    </p>
                </footer>
            </aside>
        </>
    );
}
